// Client Portal Navigation Component
// BP-10-04: Attorney Frontend

import { NavLink, useParams } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  FileText,
  MessageSquare,
  User,
  Phone,
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import type { LegalCase } from '@/features/attorney/types/attorney';

interface ClientPortalNavProps {
  caseData?: LegalCase;
}

const navItems = [
  { to: '', icon: LayoutDashboard, label: 'Overview', end: true },
  { to: 'timeline', icon: Calendar, label: 'Timeline' },
  { to: 'documents', icon: FileText, label: 'Documents' },
  { to: 'messages', icon: MessageSquare, label: 'Messages' },
];

export function ClientPortalNav({ caseData }: ClientPortalNavProps) {
  const { caseId } = useParams();

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Attorney Info Header */}
      {caseData?.attorney && (
        <div className="p-4 bg-sage/5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-sage/20 flex items-center justify-center">
              <User className="w-6 h-6 text-sage" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-navy truncate">
                Your Attorney
              </p>
              <p className="text-base font-semibold text-navy truncate">
                {caseData.attorney.firstName} {caseData.attorney.lastName}
              </p>
            </div>
          </div>
          
          {/* Contact Button */}
          <a
            href={`mailto:${caseData.attorney.email}`}
            className="mt-3 flex items-center justify-center gap-2 w-full py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors text-sm"
          >
            <Phone className="w-4 h-4" />
            Contact Attorney
          </a>
        </div>
      )}

      {/* Navigation Links */}
      <nav className="p-2">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={`/family/legal-case${item.to ? `/${item.to}` : ''}`}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                isActive
                  ? 'bg-sage text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              )
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="font-medium">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Case Info Footer */}
      {caseData && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
            Case Number
          </div>
          <div className="text-sm font-medium text-navy">
            {caseData.caseNumber || caseData.internalReference || 'N/A'}
          </div>
          
          {caseData.filedDate && (
            <div className="mt-2">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                Filed
              </div>
              <div className="text-sm text-gray-700">
                {new Date(caseData.filedDate).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ClientPortalNav;

